import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  getCampaigns as getCampaignsRequest,
  saveCampaignQuestions as saveCampaignQuestionsRequest,
} from "apis/campaign.api";
import { Path } from "enums";
import styles from "./survey-builder.page.module.scss";

const SURVEY_GUIDE_STORAGE_PREFIX = "survey-guide-draft:";
const ROUTE_END = "__END__";
const ROUTE_CONTINUE = "__CONTINUE__";
const SURVEY_GUIDE_DOCUMENT_VERSION = 1;

const ANSWER_TYPES = [
  { value: "shortText", label: "Short Field", supportsOptions: false },
  { value: "multipleChoice", label: "Multiple Choice", supportsOptions: true },
];

const MAP_DIMENSIONS = {
  nodeWidth: 200,
  nodeHeight: 58,
  endWidth: 164,
  endHeight: 32,
  laneGap: 232,
  rowGap: 124,
  paddingX: 88,
  paddingY: 42,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const formatCampaignDate = (value) => {
  if (!value) return "-";
  const asString = String(value);
  return asString.includes("T") ? asString.split("T")[0] : asString;
};

const normalizeCampaign = (campaign) => {
  if (!campaign) return null;

  const code = campaign?.campaignCode || campaign?.code || "";
  const rawCampaignType = String(campaign?.campaignType || "").toLowerCase();
  const rawSurveyType = String(campaign?.surveyType || campaign?.type || "single").toLowerCase();
  const surveyType =
    rawCampaignType === "custom_survey" || rawSurveyType === "custom" ? "custom" : "single";

  return {
    campaignId: campaign?.campaignId,
    code,
    title: campaign?.description || campaign?.name || code,
    date: formatCampaignDate(campaign?.campaignDate || campaign?.date),
    location: campaign?.location || "Unknown",
    surveyType,
    surveyQuestionDocument: campaign?.surveyQuestionDocument || "",
  };
};

const mergeCampaignFromRoute = (campaign, routedCampaign) => {
  if (!campaign) return campaign;

  const campaignCode = String(campaign?.code || "").toLowerCase();
  const routedCode = String(routedCampaign?.code || "").toLowerCase();

  if (!routedCode || campaignCode !== routedCode) return campaign;

  return {
    ...campaign,
    surveyType:
      routedCampaign?.surveyType === "custom" ? routedCampaign.surveyType : campaign.surveyType,
    title: routedCampaign?.title || campaign.title,
  };
};

const createQuestionId = () => `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createOptionId = () => `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createNoteId = () => `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getAnswerTypeMeta = (value) =>
  ANSWER_TYPES.find((entry) => entry.value === value) || ANSWER_TYPES[0];

const getQuestionCodeLabel = (question, index) =>
  String(question?.code || `q${index + 1}`).toUpperCase();

const truncateText = (value, maxLength = 88) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "Untitled question";
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
};

const getQuestionAnswerChip = (question) => {
  const answerTypeMeta = getAnswerTypeMeta(question?.answerType);
  if (!answerTypeMeta.supportsOptions) return "short field";

  const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
  return `${optionCount || 0}-choice`;
};

const getQuestionVisualTone = (question, isStart) => {
  if (isStart) {
    return {
      accent: "#10b981",
      accentSoft: "rgba(16,185,129,0.16)",
      border: "rgba(16,185,129,0.62)",
    };
  }

  return getAnswerTypeMeta(question?.answerType).supportsOptions
    ? {
        accent: "#f59e0b",
        accentSoft: "rgba(245,158,11,0.16)",
        border: "rgba(96,165,250,0.16)",
      }
    : {
        accent: "#3b82f6",
        accentSoft: "rgba(59,130,246,0.16)",
        border: "rgba(96,165,250,0.16)",
      };
};

const getLaneOffset = (lane) => {
  if (!lane) return 0;
  const distance = Math.ceil(lane / 2);
  return lane % 2 === 1 ? -distance : distance;
};

const createEmptyChoiceOptions = () => [
  { id: createOptionId(), label: "", routeTo: ROUTE_CONTINUE },
  { id: createOptionId(), label: "", routeTo: ROUTE_CONTINUE },
];

const createYesNoOptions = () => [
  { id: createOptionId(), label: "Yes", routeTo: ROUTE_CONTINUE },
  { id: createOptionId(), label: "No", routeTo: ROUTE_CONTINUE },
];

const resolveStartQuestionId = (startQuestionId, questions) => {
  if (!questions.length) return null;
  return questions.some((question) => question.id === startQuestionId)
    ? startQuestionId
    : questions[0].id;
};

const resolveRouteTarget = (routeTo, questions, index) => {
  const fallbackRoute = getSequentialRouteTarget(questions, index);
  if (!routeTo || routeTo === ROUTE_CONTINUE) return fallbackRoute;
  return routeTo;
};

const getRouteValue = (routeTo, ids, fallbackRoute) => {
  if (!routeTo || routeTo === ROUTE_CONTINUE) return ROUTE_CONTINUE;
  if (routeTo === ROUTE_END) return ROUTE_END;
  return ids.has(routeTo) ? routeTo : fallbackRoute === ROUTE_END ? ROUTE_CONTINUE : ROUTE_CONTINUE;
};

const serializeSurveyGuide = (guide, campaign) =>
  JSON.stringify({
    version: SURVEY_GUIDE_DOCUMENT_VERSION,
    campaignCode: campaign?.code || "",
    surveyType: "custom",
    savedAt: new Date().toISOString(),
    guide,
  });

const toApiQuestionsPayload = (guide) => {
  const questions = guide?.questions || [];
  const startQuestion = questions[0];

  const idToKey = new Map(questions.map((q, index) => [q.id, q.code || `q${index + 1}`]));

  const resolveNextKey = (routeTo, fallbackRouteTo) => {
    const effectiveRoute =
      !routeTo || routeTo === ROUTE_CONTINUE ? fallbackRouteTo || ROUTE_END : routeTo;

    if (!effectiveRoute || effectiveRoute === ROUTE_END) return null;
    return idToKey.get(effectiveRoute) || null;
  };

  const TYPE_MAP = {
    multipleChoice: "multiple_choice",
    shortText: "acknowledgement",
    multiple_choice: "multiple_choice",
    acknowledgement: "acknowledgement",
  };

  return {
    startKey: startQuestion ? idToKey.get(startQuestion.id) || "q1" : null,
    questions: questions.map((q, index) => {
      const key = idToKey.get(q.id) || `q${index + 1}`;
      const supportsOptions = getAnswerTypeMeta(q.answerType).supportsOptions;
      const defaultNextRouteTo = questions[index + 1]?.id || ROUTE_END;

      return {
        key,
        type: TYPE_MAP[q.answerType] || (supportsOptions ? "multiple_choice" : "acknowledgement"),
        section: q.section || "",
        prompt: q.prompt || "",
        helperText: q.interviewerNotes?.[0]?.text || "",
        interviewerNote: q.interviewerNotes?.[0]?.text || "",
        position: index + 1,
        nextKey: supportsOptions ? null : resolveNextKey(q.routeTo, defaultNextRouteTo),
        options: supportsOptions
          ? (q.options || []).map((opt, optIndex) => ({
              label: opt.label || "",
              position: optIndex + 1,
              nextKey: resolveNextKey(opt.routeTo, defaultNextRouteTo),
            }))
          : [],
      };
    }),
  };
};

const parseSurveyGuideDocument = (rawDocument) => {
  if (!rawDocument) return null;

  const parsed = typeof rawDocument === "string" ? JSON.parse(rawDocument) : rawDocument;

  if (parsed?.guide) return parsed.guide;
  if (Array.isArray(parsed?.questions)) return parsed;
  return null;
};

const buildRoutePath = (startX, startY, endX, endY) => {
  const midpointY = startY + Math.max(34, (endY - startY) / 2);
  if (Math.abs(endX - startX) < 18) {
    return `M ${startX} ${startY} L ${endX} ${endY}`;
  }

  return [
    `M ${startX} ${startY}`,
    `C ${startX} ${midpointY}, ${endX} ${midpointY}, ${endX} ${endY}`,
  ].join(" ");
};

const buildArrowPolygonPoints = (x, y) => `${x - 4.5},${y - 8} ${x + 4.5},${y - 8} ${x},${y - 1}`;

const getMapFitScale = (graph, viewport, maxScale) => {
  if (!graph?.width || !graph?.height || !viewport) return 1;

  const availableWidth = Math.max(0, viewport.clientWidth - 32);
  const availableHeight = Math.max(0, viewport.clientHeight - 32);
  if (!availableWidth || !availableHeight) return 1;

  const widthScale = availableWidth / graph.width;
  const heightScale = availableHeight / graph.height;
  return clamp(Math.min(widthScale, heightScale), 0.36, maxScale);
};

const getViewportCenterScroll = (graph, viewport, scale) => {
  if (!graph?.width || !graph?.height || !viewport) {
    return { left: 0, top: 0 };
  }

  const contentWidth = graph.width * scale;
  const contentHeight = graph.height * scale;

  return {
    left: Math.max(0, (contentWidth - viewport.clientWidth) / 2),
    top: Math.max(0, (contentHeight - viewport.clientHeight) / 2),
  };
};

const buildSurveyFlowGraph = (questions) => {
  if (!Array.isArray(questions) || !questions.length) {
    return {
      nodes: [],
      edges: [],
      width: MAP_DIMENSIONS.nodeWidth + MAP_DIMENSIONS.paddingX * 2,
      height: 320,
    };
  }

  const questionIndexById = new Map(questions.map((question, index) => [question.id, index]));
  const laneById = { [questions[0].id]: 0 };
  let nextLane = 1;

  const edges = [];

  questions.forEach((question, index) => {
    const currentLane = laneById[question.id] ?? 0;
    laneById[question.id] = currentLane;

    const nextSequentialId = getSequentialRouteTarget(questions, index);
    const answerTypeMeta = getAnswerTypeMeta(question.answerType);
    const questionEdges = answerTypeMeta.supportsOptions
      ? question.options.map((option) => {
          const targetId =
            !option.routeTo || option.routeTo === ROUTE_CONTINUE
              ? nextSequentialId
              : option.routeTo;

          return {
            id: `${question.id}:${option.id}`,
            sourceId: question.id,
            targetId,
            kind:
              targetId === ROUTE_END
                ? "end"
                : targetId === nextSequentialId
                  ? "sequential"
                  : "branch",
            label: option.label,
          };
        })
      : (() => {
          const targetId =
            !question.routeTo || question.routeTo === ROUTE_CONTINUE
              ? nextSequentialId
              : question.routeTo;

          return [
            {
              id: `${question.id}:direct`,
              sourceId: question.id,
              targetId,
              kind:
                targetId === ROUTE_END
                  ? "end"
                  : targetId === nextSequentialId
                    ? "sequential"
                    : "branch",
              label: "Direct response",
            },
          ];
        })();

    questionEdges.forEach((edge) => {
      if (edge.targetId !== ROUTE_END && laneById[edge.targetId] == null) {
        const targetIndex = questionIndexById.get(edge.targetId);
        const keepLane = edge.kind === "sequential" && targetIndex === index + 1;
        laneById[edge.targetId] = keepLane ? currentLane : nextLane++;
      }
    });

    edges.push(...questionEdges);
  });

  questions.forEach((question) => {
    if (laneById[question.id] == null) {
      laneById[question.id] = nextLane++;
    }
  });

  const usedLanes = Object.values(laneById);
  const laneCount = usedLanes.length ? Math.max(...usedLanes) + 1 : 1;
  const laneOffsets = Array.from({ length: laneCount }, (_, lane) => getLaneOffset(lane));
  const minOffset = Math.min(...laneOffsets);
  const maxOffset = Math.max(...laneOffsets);
  const centerX =
    MAP_DIMENSIONS.paddingX +
    Math.abs(minOffset) * MAP_DIMENSIONS.laneGap +
    MAP_DIMENSIONS.nodeWidth / 2;
  const endSourceLanes = edges
    .filter((edge) => edge.targetId === ROUTE_END)
    .map((edge) => laneById[edge.sourceId] ?? 0);
  const averageEndLane = endSourceLanes.length
    ? endSourceLanes.reduce((sum, lane) => sum + lane, 0) / endSourceLanes.length
    : 0;
  const averageEndOffset =
    endSourceLanes.length > 0
      ? endSourceLanes.reduce((sum, lane) => sum + getLaneOffset(lane), 0) / endSourceLanes.length
      : 0;

  const positionedNodes = questions.map((question, index) => ({
    id: question.id,
    kind: "question",
    row: index,
    lane: laneById[question.id] ?? 0,
    width: MAP_DIMENSIONS.nodeWidth,
    height: MAP_DIMENSIONS.nodeHeight,
    x:
      centerX +
      getLaneOffset(laneById[question.id] ?? 0) * MAP_DIMENSIONS.laneGap -
      MAP_DIMENSIONS.nodeWidth / 2,
    y: MAP_DIMENSIONS.paddingY + index * MAP_DIMENSIONS.rowGap,
    index,
    question,
  }));

  const endNode = {
    id: ROUTE_END,
    kind: "end",
    row: questions.length,
    lane: averageEndLane,
    width: MAP_DIMENSIONS.endWidth,
    height: MAP_DIMENSIONS.endHeight,
    x: centerX + averageEndOffset * MAP_DIMENSIONS.laneGap - MAP_DIMENSIONS.endWidth / 2,
    y: MAP_DIMENSIONS.paddingY + questions.length * MAP_DIMENSIONS.rowGap,
  };

  const allNodes = [...positionedNodes, endNode];

  const width = Math.max(
    MAP_DIMENSIONS.paddingX * 2 +
      (maxOffset - minOffset) * MAP_DIMENSIONS.laneGap +
      MAP_DIMENSIONS.nodeWidth,
    endNode.x + endNode.width + MAP_DIMENSIONS.paddingX,
  );
  const height = endNode.y + endNode.height + MAP_DIMENSIONS.paddingY;

  const contentMinX = Math.min(...allNodes.map((node) => node.x));
  const contentMaxX = Math.max(...allNodes.map((node) => node.x + node.width));
  const contentWidth = contentMaxX - contentMinX;
  const centeredMinX = (width - contentWidth) / 2;
  const shiftX = centeredMinX - contentMinX;

  const centeredNodes = allNodes.map((node) => ({
    ...node,
    x: node.x + shiftX,
  }));

  const nodeById = centeredNodes.reduce((accumulator, node) => {
    accumulator[node.id] = node;
    return accumulator;
  }, {});

  const incomingEdgeCountByTarget = edges.reduce((accumulator, edge) => {
    accumulator[edge.targetId] = (accumulator[edge.targetId] || 0) + 1;
    return accumulator;
  }, {});
  const incomingEdgeIndexByTarget = {};

  const positionedEdges = edges
    .map((edge) => {
      const sourceNode = nodeById[edge.sourceId];
      const targetNode = nodeById[edge.targetId];
      if (!sourceNode || !targetNode) return null;

      const startX = sourceNode.x + sourceNode.width / 2;
      const startY = sourceNode.y + sourceNode.height;
      const incomingCount = incomingEdgeCountByTarget[edge.targetId] || 1;
      const incomingIndex = incomingEdgeIndexByTarget[edge.targetId] || 0;
      incomingEdgeIndexByTarget[edge.targetId] = incomingIndex + 1;
      const spread =
        incomingCount > 1
          ? (incomingIndex - (incomingCount - 1) / 2) *
            Math.min(42, (targetNode.width - 26) / incomingCount)
          : 0;
      const endX = targetNode.x + targetNode.width / 2 + spread;
      const endY = targetNode.y;

      return {
        ...edge,
        path: buildRoutePath(startX, startY, endX, endY),
        arrowX: endX,
        arrowY: endY,
        labelX: startX + (endX - startX) / 2,
        labelY: startY + Math.max(22, (endY - startY) / 2) - 10,
      };
    })
    .filter(Boolean);

  return {
    nodes: centeredNodes,
    edges: positionedEdges,
    width,
    height,
  };
};

const SurveyFlowCanvas = ({
  graph,
  scale,
  variant,
  showBranchLabels,
  selectedQuestionId,
  startQuestionId,
  onNodeSelect,
}) => {
  const startNode =
    graph.nodes.find((node) => node.id === startQuestionId) || graph.nodes[0] || null;
  const onNodeKeyDown = (event, nodeId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onNodeSelect(nodeId);
    }
  };

  return (
    <div className={styles.flowMapViewportInner}>
      <div
        className={styles.flowMapScaleBox}
        style={{ width: graph.width * scale, height: graph.height * scale }}
      >
        <div
          className={styles.flowMapCanvas}
          style={{
            width: graph.width,
            height: graph.height,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          <svg
            className={styles.flowMapSvg}
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            preserveAspectRatio="xMidYMin meet"
          >
            {startNode && (
              <>
                <path
                  d={`M ${startNode.x + startNode.width / 2} ${MAP_DIMENSIONS.paddingY - 12} L ${startNode.x + startNode.width / 2} ${startNode.y}`}
                  className={`${styles.flowRoute} ${styles.flowRouteStart}`}
                />
                <polygon
                  points={buildArrowPolygonPoints(startNode.x + startNode.width / 2, startNode.y)}
                  fill="#10b981"
                />
                <text
                  x={startNode.x + startNode.width / 2}
                  y={MAP_DIMENSIONS.paddingY - 28}
                  textAnchor="middle"
                  className={styles.flowStartLabel}
                >
                  ENTRY
                </text>
              </>
            )}

            {graph.edges.map((edge) => (
              <g key={edge.id}>
                <path
                  d={edge.path}
                  className={`${styles.flowRoute} ${
                    edge.kind === "sequential"
                      ? styles.flowRouteSequential
                      : edge.kind === "branch"
                        ? styles.flowRouteBranch
                        : styles.flowRouteEnd
                  }`}
                />
                <polygon
                  points={buildArrowPolygonPoints(edge.arrowX, edge.arrowY)}
                  fill={
                    edge.kind === "sequential"
                      ? "#3b82f6"
                      : edge.kind === "branch"
                        ? "#f59e0b"
                        : "#10b981"
                  }
                />
                {showBranchLabels && edge.kind === "branch" && edge.label ? (
                  <g>
                    <rect
                      x={edge.labelX - 44}
                      y={edge.labelY - 12}
                      width="88"
                      height="16"
                      rx="8"
                      className={styles.flowRouteLabelBg}
                    />
                    <text
                      x={edge.labelX}
                      y={edge.labelY}
                      textAnchor="middle"
                      className={styles.flowRouteText}
                    >
                      {truncateText(edge.label, 18)}
                    </text>
                  </g>
                ) : null}
              </g>
            ))}

            {graph.nodes.map((node) => {
              if (node.kind === "end") {
                return (
                  <g key={node.id} className={styles.flowEndNodeGroup}>
                    <rect
                      x={node.x}
                      y={node.y}
                      width={node.width}
                      height={node.height}
                      rx="16"
                      className={styles.flowEndNodeRect}
                    />
                    <text
                      x={node.x + node.width / 2}
                      y={node.y + 20}
                      textAnchor="middle"
                      className={styles.flowEndNodeText}
                    >
                      ■ End of Survey
                    </text>
                  </g>
                );
              }

              const { question, index } = node;
              const isStart = startQuestionId === node.id;
              const tone = getQuestionVisualTone(question, isStart);
              const isSelected = selectedQuestionId === node.id;
              const codeLabel = isStart
                ? `${getQuestionCodeLabel(question, index)} · START`
                : getQuestionCodeLabel(question, index);

              return (
                <g
                  key={node.id}
                  className={`${styles.flowQuestionGroup} ${
                    isSelected ? styles.flowQuestionGroupSelected : ""
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNodeSelect(node.id)}
                  onKeyDown={(event) => onNodeKeyDown(event, node.id)}
                >
                  <rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    rx="11"
                    className={styles.flowQuestionCardRect}
                    style={{ stroke: isSelected ? "rgba(147,197,253,0.95)" : tone.border }}
                  />
                  <rect
                    x={node.x}
                    y={node.y}
                    width="4"
                    height={node.height}
                    rx="2"
                    style={{ fill: tone.accent }}
                  />
                  <text
                    x={node.x + 15}
                    y={node.y + 19}
                    className={styles.flowQuestionCodeText}
                    style={{ fill: tone.accent }}
                  >
                    {codeLabel}
                  </text>
                  <rect
                    x={node.x + node.width - 80}
                    y={node.y + 8}
                    width="68"
                    height="16"
                    rx="8"
                    style={{ fill: tone.accentSoft }}
                  />
                  <text
                    x={node.x + node.width - 46}
                    y={node.y + 19.5}
                    textAnchor="middle"
                    className={styles.flowQuestionChipText}
                    style={{ fill: tone.accent }}
                  >
                    {getQuestionAnswerChip(question)}
                  </text>
                  <text x={node.x + 15} y={node.y + 35} className={styles.flowQuestionSectionText}>
                    {truncateText(question.section, 28)}
                  </text>
                  <text x={node.x + 15} y={node.y + 50} className={styles.flowQuestionPromptText}>
                    {truncateText(question.prompt, 36)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
};

const createDefaultQuestion = ({
  section,
  prompt,
  answerType,
  interviewerNotes = [],
  options,
  routeTo = ROUTE_CONTINUE,
}) => ({
  id: createQuestionId(),
  code: "",
  section,
  prompt,
  answerType,
  interviewerNotes: interviewerNotes.map((note) => ({ id: createNoteId(), text: note })),
  options: options || (answerType === "multipleChoice" ? createEmptyChoiceOptions() : []),
  routeTo,
  expanded: true,
});

const createDefaultGuide = (campaign) => ({
  title: `${campaign?.title || campaign?.code || "Campaign"} Survey Guide`,
  startQuestionId: null,
  questions: [],
});

const getSequentialRouteTarget = (questions, index) => questions[index + 1]?.id || ROUTE_END;

const normalizeAnswerType = (rawType) => {
  if (rawType === "singleChoice") return "multipleChoice";
  return ANSWER_TYPES.some((entry) => entry.value === rawType) ? rawType : "shortText";
};

const normalizeInterviewerNotes = (question) => {
  if (Array.isArray(question?.interviewerNotes)) {
    return question.interviewerNotes
      .map((note) => {
        if (typeof note === "string") {
          return { id: createNoteId(), text: note };
        }

        return {
          id: note?.id || createNoteId(),
          text: String(note?.text || ""),
        };
      })
      .slice(0, 1);
  }

  if (typeof question?.interviewerNote === "string" && question.interviewerNote.trim()) {
    return [{ id: createNoteId(), text: question.interviewerNote }];
  }

  if (typeof question?.helperText === "string" && question.helperText.trim()) {
    return [{ id: createNoteId(), text: question.helperText }];
  }

  return [];
};

const normalizeOptions = (question, answerType, fallbackRoute) => {
  if (!getAnswerTypeMeta(answerType).supportsOptions) return [];

  const rawOptions = Array.isArray(question?.options)
    ? question.options
    : typeof question?.options === "string"
      ? question.options.split("\n")
      : [];

  const normalized = rawOptions.map((option) => {
    if (typeof option === "string") {
      return { id: createOptionId(), label: option, routeTo: fallbackRoute };
    }

    return {
      id: option?.id || createOptionId(),
      label: option?.label != null ? String(option.label) : String(option?.value ?? ""),
      routeTo: option?.routeTo || option?.nextQuestionId || ROUTE_CONTINUE,
    };
  });

  if (normalized.length) return normalized;
  return createEmptyChoiceOptions();
};

const sanitizeQuestions = (questions) => {
  const ids = new Set(questions.map((question) => question.id));

  return questions.map((question, index) => {
    const answerType = normalizeAnswerType(question?.answerType || question?.type);
    const fallbackRoute = getSequentialRouteTarget(questions, index);
    const routeTo = getRouteValue(question?.routeTo, ids, fallbackRoute);
    const options = normalizeOptions(question, answerType, fallbackRoute).map((option) => ({
      ...option,
      routeTo: getRouteValue(option.routeTo, ids, fallbackRoute),
    }));

    return {
      id: question?.id || createQuestionId(),
      code: `q${index + 1}`,
      section: question?.section != null ? String(question.section) : "Untitled Section",
      prompt:
        question?.prompt != null
          ? String(question.prompt)
          : question?.title != null
            ? String(question.title)
            : `Question ${index + 1}`,
      answerType,
      interviewerNotes: normalizeInterviewerNotes(question),
      options,
      routeTo: getAnswerTypeMeta(answerType).supportsOptions ? ROUTE_CONTINUE : routeTo,
      expanded: question?.expanded !== false || index === 0,
    };
  });
};

const normalizeGuide = (guide, campaign) => {
  const fallback = createDefaultGuide(campaign);
  const rawQuestions = Array.isArray(guide?.questions) ? guide.questions : fallback.questions;
  const questions = sanitizeQuestions(rawQuestions);

  return {
    title: String(guide?.title || fallback.title),
    startQuestionId: resolveStartQuestionId(guide?.startQuestionId, questions),
    questions,
  };
};

const getQuestionRouteTargets = (question, questions, index) => {
  if (getAnswerTypeMeta(question.answerType).supportsOptions) {
    return question.options.map((option) => resolveRouteTarget(option.routeTo, questions, index));
  }

  return [resolveRouteTarget(question.routeTo, questions, index)];
};

const allPathsReachEnd = (questions, startQuestionId) => {
  if (!questions.length || !startQuestionId) return false;

  const questionById = new Map(questions.map((question) => [question.id, question]));
  const indexById = new Map(questions.map((question, index) => [question.id, index]));
  const memo = new Map();

  const visit = (questionId, visiting) => {
    if (questionId === ROUTE_END) return true;
    if (!questionById.has(questionId)) return false;
    if (memo.has(questionId)) return memo.get(questionId);
    if (visiting.has(questionId)) return false;

    visiting.add(questionId);
    const question = questionById.get(questionId);
    const index = indexById.get(questionId);
    const targets = getQuestionRouteTargets(question, questions, index);
    const result = targets.length > 0 && targets.every((targetId) => visit(targetId, visiting));
    visiting.delete(questionId);
    memo.set(questionId, result);
    return result;
  };

  return visit(startQuestionId, new Set());
};

const getStorageKey = (campaignCode) => `${SURVEY_GUIDE_STORAGE_PREFIX}${campaignCode}`;

const isLegacySeededQuestions = (questions) => {
  if (!Array.isArray(questions) || questions.length !== 3) return false;

  const prompts = questions.map((question) =>
    String(question?.prompt || question?.title || "")
      .toLowerCase()
      .trim(),
  );

  const hasConsentPrompt = prompts.some((prompt) => prompt.includes("data privacy act"));
  const hasAgePrompt = prompts.some((prompt) => prompt.includes("ilang taon"));
  const hasCandidatePrompt = prompts.some((prompt) => prompt.includes("malakas na kandidato"));

  return hasConsentPrompt && hasAgePrompt && hasCandidatePrompt;
};

const normalizeGuideWithoutLegacySeed = (guide, campaign) => {
  const normalized = normalizeGuide(guide, campaign);

  if (isLegacySeededQuestions(normalized.questions)) {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }

  return normalized;
};

const readStoredGuide = async (campaign) => {
  if (!campaign?.code) {
    return createDefaultGuide(campaign);
  }

  if (campaign?.surveyQuestionDocument) {
    try {
      const response = await fetch(campaign.surveyQuestionDocument);
      if (response.ok) {
        const remoteGuide = parseSurveyGuideDocument(await response.text());
        if (remoteGuide) {
          return normalizeGuideWithoutLegacySeed(remoteGuide, campaign);
        }
      }
    } catch {
      // Fall back to local cache below when remote fetch is unavailable.
    }
  }

  if (typeof window === "undefined") {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }

  try {
    const stored = window.localStorage.getItem(getStorageKey(campaign.code));
    if (!stored) return normalizeGuide(createDefaultGuide(campaign), campaign);
    return normalizeGuideWithoutLegacySeed(
      parseSurveyGuideDocument(stored) || JSON.parse(stored),
      campaign,
    );
  } catch {
    return normalizeGuide(createDefaultGuide(campaign), campaign);
  }
};

const SurveyBuilderPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { campaignCode } = useParams();
  const questionRefs = useRef({});
  const pendingQuestionScrollIdRef = useRef(null);
  const inlineMapViewportRef = useRef(null);
  const expandedMapViewportRef = useRef(null);

  const routedCampaign = useMemo(
    () => normalizeCampaign(location.state?.campaign),
    [location.state],
  );
  const routedQuestions = useMemo(
    () => (Array.isArray(location.state?.questions) ? location.state.questions : null),
    [location.state],
  );
  const [campaign, setCampaign] = useState(() =>
    routedCampaign?.code?.toLowerCase() === String(campaignCode || "").toLowerCase()
      ? routedCampaign
      : null,
  );
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(!campaign);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [inlineMapScale, setInlineMapScale] = useState(1);
  const [expandedMapScale, setExpandedMapScale] = useState(1);
  const [activeQuestionId, setActiveQuestionId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (routedCampaign?.code?.toLowerCase() === String(campaignCode || "").toLowerCase()) {
      setCampaign(routedCampaign);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    getCampaignsRequest()
      .then((data) => {
        if (!isMounted) return;

        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
            ? data
            : [];

        const match = rows
          .map((item) => normalizeCampaign(item))
          .find(
            (item) =>
              String(item?.code || "").toLowerCase() === String(campaignCode || "").toLowerCase(),
          );

        if (!match) {
          setCampaign(null);
          setError("Campaign not found.");
          return;
        }

        setCampaign(mergeCampaignFromRoute(match, routedCampaign));
      })
      .catch(() => {
        if (!isMounted) return;
        setCampaign(null);
        setError("Failed to load survey guide.");
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [campaignCode, routedCampaign]);

  useEffect(() => {
    let isMounted = true;

    if (!campaign?.code) return undefined;

    readStoredGuide(campaign)
      .then((nextGuide) => {
        if (!isMounted) return;

        const hasStoredQuestions =
          Array.isArray(nextGuide?.questions) && nextGuide.questions.length > 0;

        const hasUsableRoutedQuestions =
          Array.isArray(routedQuestions) &&
          routedQuestions.length > 0 &&
          !isLegacySeededQuestions(routedQuestions);

        if (!hasStoredQuestions && hasUsableRoutedQuestions) {
          setGuide(normalizeGuide({ questions: routedQuestions }, campaign));
          setSaveState("idle");
          setSaveFeedback(null);
        } else {
          setGuide(normalizeGuide(nextGuide, campaign));
          setSaveState("saved");
          setSaveFeedback(null);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setGuide(normalizeGuide(createDefaultGuide(campaign), campaign));
        setSaveState("idle");
        setSaveFeedback("Unable to load a saved guide. Started from the local draft.");
      });

    return () => {
      isMounted = false;
    };
  }, [campaign, routedQuestions]);

  useEffect(() => {
    const questionIds = new Set((guide?.questions || []).map((question) => question.id));
    setActiveQuestionId((prev) => {
      if (prev && questionIds.has(prev)) return prev;
      return guide?.questions?.[0]?.id || null;
    });
  }, [guide]);

  const goBackToCampaign = () => {
    if (saveState === "idle") {
      const shouldLeave = window.confirm(
        "You have unsaved survey guide changes. Leave the builder without saving?",
      );

      if (!shouldLeave) return;
    }

    const code = String(campaign?.code || campaignCode || "").trim();
    if (!code) {
      history.push(Path.Campaigns);
      return;
    }

    history.push(`/${encodeURIComponent(code)}`);
  };

  const updateQuestions = (updater) => {
    setGuide((prev) => {
      const questions = sanitizeQuestions(updater(prev.questions));

      return {
        ...prev,
        questions,
        startQuestionId: resolveStartQuestionId(prev.startQuestionId, questions),
      };
    });
    setSaveState("idle");
    setSaveFeedback(null);
  };

  const setStartQuestion = (questionId) => {
    setGuide((prev) => ({ ...prev, startQuestionId: questionId }));
    setSaveState("idle");
    setSaveFeedback(null);
  };

  const onQuestionChange = (questionId, field, value) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId ? { ...question, [field]: value } : question,
      ),
    );
  };

  const onQuestionTypeChange = (questionId, answerType) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;

        const supportsOptions = getAnswerTypeMeta(answerType).supportsOptions;
        return {
          ...question,
          answerType,
          options: supportsOptions
            ? question.options.length
              ? question.options
              : createEmptyChoiceOptions()
            : [],
          routeTo: supportsOptions ? ROUTE_CONTINUE : question.routeTo,
        };
      }),
    );
  };

  const applyYesNoTemplate = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answerType: "multipleChoice",
              options: createYesNoOptions(),
            }
          : question,
      ),
    );
  };

  const addQuestion = () => {
    const question = createDefaultQuestion({
      section: guide?.questions?.[guide.questions.length - 1]?.section || "New Section",
      prompt: "",
      answerType: "shortText",
      interviewerNotes: [],
    });

    pendingQuestionScrollIdRef.current = question.id;

    setGuide((prev) => {
      const questions = sanitizeQuestions([...(prev?.questions || []), question]);
      return {
        ...(prev || { title: "Survey Guide" }),
        questions,
        startQuestionId:
          prev?.startQuestionId || questions.length === 1
            ? resolveStartQuestionId(prev?.startQuestionId || question.id, questions)
            : resolveStartQuestionId(prev?.startQuestionId, questions),
      };
    });

    setSaveState("idle");
    setSaveFeedback(null);
    setActiveQuestionId(question.id);
  };

  const removeQuestion = (questionId) => {
    const questionLabel = guide?.questions?.find((question) => question.id === questionId)?.code;
    const shouldDelete = window.confirm(
      `Delete ${String(questionLabel || "this question").toUpperCase()} and reset any routes pointing to it to Continue?`,
    );

    if (!shouldDelete) return;

    updateQuestions((questions) => questions.filter((question) => question.id !== questionId));
  };

  const moveQuestion = (questionId, direction) => {
    updateQuestions((questions) => {
      const index = questions.findIndex((question) => question.id === questionId);
      const targetIndex = index + direction;
      if (index < 0 || targetIndex < 0 || targetIndex >= questions.length) return questions;

      const next = [...questions];
      const [question] = next.splice(index, 1);
      next.splice(targetIndex, 0, question);
      return next;
    });
  };

  const addQuestionOption = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              options: [
                ...question.options,
                {
                  id: createOptionId(),
                  label: "",
                  routeTo: ROUTE_CONTINUE,
                },
              ],
            }
          : question,
      ),
    );
  };

  const updateQuestionOption = (questionId, optionId, field, value) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        return {
          ...question,
          options: question.options.map((option) =>
            option.id === optionId ? { ...option, [field]: value } : option,
          ),
        };
      }),
    );
  };

  const removeQuestionOption = (questionId, optionId) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        return {
          ...question,
          options: question.options.filter((option) => option.id !== optionId),
        };
      }),
    );
  };

  const addInterviewerNote = (questionId) => {
    updateQuestions((questions) =>
      questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              interviewerNotes: [{ id: createNoteId(), text: "" }],
            }
          : question,
      ),
    );
  };

  const updateInterviewerNote = (questionId, noteId, value) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        const currentNote = question.interviewerNotes[0];
        if (!currentNote || currentNote.id !== noteId) return question;
        return {
          ...question,
          interviewerNotes: [{ ...currentNote, text: value }],
        };
      }),
    );
  };

  const removeInterviewerNote = (questionId, noteId) => {
    updateQuestions((questions) =>
      questions.map((question) => {
        if (question.id !== questionId) return question;
        const currentNote = question.interviewerNotes[0];
        if (!currentNote || currentNote.id !== noteId) return question;
        return {
          ...question,
          interviewerNotes: [],
        };
      }),
    );
  };

  const onSaveGuide = () => {
    if (!campaign?.code || !guide || typeof window === "undefined") return;
    if (!guide.questions.length) {
      setSaveState("error");
      setSaveFeedback("Add at least one question before saving this survey guide.");
      return;
    }

    const payload = normalizeGuide(guide, campaign);
    const serializedGuide = serializeSurveyGuide(payload, campaign);

    try {
      window.localStorage.setItem(getStorageKey(campaign.code), serializedGuide);
    } catch {
      setSaveState("error");
      setSaveFeedback("Failed to save the local draft.");
      return;
    }

    setSaveState("saving");
    setSaveFeedback(null);

    const apiQuestionsPayload = toApiQuestionsPayload(payload);

    const saveToApi = campaign.campaignId
      ? saveCampaignQuestionsRequest({
          campaignId: campaign.campaignId,
          payload: apiQuestionsPayload,
        })
      : Promise.resolve(null);

    saveToApi
      .then(() => {
        setGuide(payload);
        setSaveState("saved");
        setSaveFeedback("Survey guide saved to this campaign.");
      })
      .catch(() => {
        setGuide(payload);
        setSaveState("error");
        setSaveFeedback("Saved locally, but syncing survey questions to backend failed.");
      });
  };

  const onResetGuide = () => {
    setGuide(normalizeGuide(createDefaultGuide(campaign), campaign));
    setSaveState("idle");
  };

  const questionCount = guide?.questions?.length || 0;
  const hasUnsavedChanges = saveState === "idle";
  const sectionsCount = useMemo(() => {
    const sections = new Set((guide?.questions || []).map((question) => question.section.trim()));
    return sections.size;
  }, [guide]);
  const routingCount = useMemo(
    () =>
      (guide?.questions || []).reduce((sum, question) => {
        if (getAnswerTypeMeta(question.answerType).supportsOptions) {
          return (
            sum +
            question.options.filter((option) => option.routeTo && option.routeTo !== ROUTE_END)
              .length
          );
        }
        return sum + (question.routeTo && question.routeTo !== ROUTE_END ? 1 : 0);
      }, 0),
    [guide],
  );

  const routeOptions = useMemo(() => {
    const base = (guide?.questions || []).map((question) => ({
      value: question.id,
      label: `Jump to ${question.code.toUpperCase()}: ${truncateText(question.prompt, 42)}`,
    }));

    return [
      { value: ROUTE_CONTINUE, label: "Continue (next question)" },
      { value: ROUTE_END, label: "End survey" },
      ...base,
    ];
  }, [guide]);

  const validation = useMemo(() => {
    const questions = guide?.questions || [];
    const missingPromptIds = [];
    const missingOptionLabelsByQuestion = {};

    questions.forEach((question) => {
      if (!question.prompt.trim()) {
        missingPromptIds.push(question.id);
      }

      if (getAnswerTypeMeta(question.answerType).supportsOptions) {
        const missingLabels = question.options.filter((option) => !option.label.trim()).length;
        if (missingLabels) {
          missingOptionLabelsByQuestion[question.id] = missingLabels;
        }
      }
    });

    const hasStart = Boolean(
      guide?.startQuestionId && questions.some((q) => q.id === guide.startQuestionId),
    );
    const pathsReachEnd = allPathsReachEnd(questions, guide?.startQuestionId);

    return {
      hasStart,
      pathsReachEnd,
      missingPromptIds,
      missingOptionLabelsByQuestion,
      perQuestion: questions.reduce((accumulator, question) => {
        const issues = [];

        if (missingPromptIds.includes(question.id)) {
          issues.push("Question text is required.");
        }

        if (missingOptionLabelsByQuestion[question.id]) {
          issues.push(
            `${missingOptionLabelsByQuestion[question.id]} choice option${missingOptionLabelsByQuestion[question.id] === 1 ? " is" : "s are"} missing a label.`,
          );
        }

        accumulator[question.id] = issues;
        return accumulator;
      }, {}),
      global: [
        !hasStart && questions.length ? "Select one question as the survey start." : null,
        missingPromptIds.length
          ? `${missingPromptIds.length} question${missingPromptIds.length === 1 ? " is" : "s are"} missing text.`
          : null,
        Object.keys(missingOptionLabelsByQuestion).length
          ? "Every multiple-choice option needs a label before launch."
          : null,
        questions.length && !pathsReachEnd
          ? "Some routes do not currently reach End survey."
          : null,
      ].filter(Boolean),
    };
  }, [guide]);

  const readinessChecks = useMemo(() => {
    return [
      {
        label: "Survey is ready to save",
        passed:
          questionCount > 0 &&
          !validation.missingPromptIds.length &&
          !Object.keys(validation.missingOptionLabelsByQuestion).length,
      },
      { label: "Start question set", passed: validation.hasStart },
      { label: "Every path can reach the end", passed: validation.pathsReachEnd },
    ];
  }, [questionCount, validation]);

  const flowGraph = useMemo(() => buildSurveyFlowGraph(guide?.questions || []), [guide]);

  const startMapNode = useMemo(
    () => flowGraph.nodes.find((node) => node.id === guide?.startQuestionId) || flowGraph.nodes[0],
    [flowGraph, guide],
  );

  useEffect(() => {
    if (!pendingQuestionScrollIdRef.current) return;

    const questionId = pendingQuestionScrollIdRef.current;
    pendingQuestionScrollIdRef.current = null;

    window.requestAnimationFrame(() => {
      const target = questionRefs.current[questionId];
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [guide?.questions]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const fitExpandedMap = () => {
    if (!expandedMapViewportRef.current) return;
    const nextScale = getMapFitScale(flowGraph, expandedMapViewportRef.current, 1);
    setExpandedMapScale(nextScale);

    window.requestAnimationFrame(() => {
      if (!expandedMapViewportRef.current) return;

      const centerScroll = getViewportCenterScroll(
        flowGraph,
        expandedMapViewportRef.current,
        nextScale,
      );

      expandedMapViewportRef.current.scrollTo({
        left: centerScroll.left,
        top: centerScroll.top,
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    const syncInlineMapScale = () => {
      if (!inlineMapViewportRef.current) return;

      const nextScale = getMapFitScale(flowGraph, inlineMapViewportRef.current, 0.82);
      setInlineMapScale(nextScale);

      window.requestAnimationFrame(() => {
        if (!inlineMapViewportRef.current) return;

        const centerScroll = getViewportCenterScroll(
          flowGraph,
          inlineMapViewportRef.current,
          nextScale,
        );

        inlineMapViewportRef.current.scrollTo({
          left: centerScroll.left,
          top: centerScroll.top,
          behavior: "auto",
        });
      });
    };

    syncInlineMapScale();

    if (typeof ResizeObserver === "undefined" || !inlineMapViewportRef.current) return undefined;

    const observer = new ResizeObserver(() => syncInlineMapScale());
    observer.observe(inlineMapViewportRef.current);

    return () => observer.disconnect();
  }, [flowGraph]);

  useEffect(() => {
    if (!mapExpanded) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      if (!expandedMapViewportRef.current) return;

      const nextScale = getMapFitScale(flowGraph, expandedMapViewportRef.current, 1);
      setExpandedMapScale(nextScale);

      window.requestAnimationFrame(() => {
        if (!expandedMapViewportRef.current) return;

        const centerScroll = getViewportCenterScroll(
          flowGraph,
          expandedMapViewportRef.current,
          nextScale,
        );

        expandedMapViewportRef.current.scrollTo({
          left: centerScroll.left,
          top: centerScroll.top,
          behavior: "smooth",
        });
      });
    });
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMapExpanded(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mapExpanded, flowGraph]);

  const scrollToQuestion = (questionId) => {
    const target = questionRefs.current[questionId];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onMapNodeSelect = (questionId) => {
    setActiveQuestionId(questionId);

    if (mapExpanded) {
      setMapExpanded(false);
      window.requestAnimationFrame(() => scrollToQuestion(questionId));
      return;
    }

    scrollToQuestion(questionId);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>Loading survey builder…</div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1>Survey builder unavailable</h1>
          <p>{error || "The requested campaign could not be loaded."}</p>
          <button
            type="button"
            className={styles.saveButton}
            onClick={() => history.push(Path.Campaigns)}
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  if (campaign.surveyType !== "custom") {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <h1>Survey builder is only available for custom surveys</h1>
          <p>
            {campaign.title} is configured as a simple survey. Switch the campaign to a custom
            survey before editing a guided questionnaire.
          </p>
          <button type="button" className={styles.saveButton} onClick={goBackToCampaign}>
            Back to Campaign
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumbs}>
        <button
          type="button"
          className={styles.breadcrumbLink}
          onClick={() => history.push(Path.Campaigns)}
        >
          My Campaigns
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <button type="button" className={styles.breadcrumbLink} onClick={goBackToCampaign}>
          {campaign.code}
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <span className={styles.breadcrumbCurrent}>Survey Builder</span>
      </div>

      <section className={styles.builderHeader}>
        <div className={styles.builderIdentity}>
          <div className={styles.campaignChip}>{campaign.code}</div>
          <div>
            <h1>{campaign.title}</h1>
            <p>Survey Builder</p>
          </div>
        </div>

        <div className={styles.builderActions}>
          <div className={styles.saveIndicator}>
            <span className={styles.saveDot} />
            {saveState === "saving"
              ? "Saving survey guide…"
              : saveState === "saved"
                ? "All changes saved"
                : saveState === "error"
                  ? "Save failed"
                  : "Unsaved changes"}
          </div>
          <button type="button" className={styles.backButton} onClick={goBackToCampaign}>
            Back
          </button>
          <button
            type="button"
            className={`${styles.saveButton} ${saveState === "saving" ? styles.saveButtonLoading : ""}`}
            onClick={onSaveGuide}
            disabled={saveState === "saving"}
          >
            {saveState === "saving" ? (
              <>
                <span className={styles.buttonSpinner} aria-hidden="true" />
                Saving Survey Guide...
              </>
            ) : (
              "Save Survey Guide"
            )}
          </button>
        </div>
      </section>

      {saveFeedback ? <div className={styles.builderMessage}>{saveFeedback}</div> : null}

      {validation.global.length ? (
        <section className={styles.validationPanel}>
          <h2>Validation feedback</h2>
          <ul className={styles.validationList}>
            {validation.global.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className={styles.builderLayout}>
        <section className={styles.canvasPanel}>
          <div className={styles.canvasTopbar}>
            <div className={styles.canvasCount}>{questionCount} questions</div>
            <div className={styles.canvasMeta}>
              <span>{sectionsCount} sections</span>
              <span>{routingCount} routing links</span>
            </div>
          </div>

          <div className={styles.questionStack}>
            {!guide?.questions?.length ? (
              <div className={styles.emptyQuestionState}>
                <strong>No questions yet</strong>
                <span>Add the first question to start building the guide and map.</span>
              </div>
            ) : null}

            {guide?.questions?.map((question, index) => {
              const answerTypeMeta = getAnswerTypeMeta(question.answerType);
              const defaultRoute =
                routeOptions.find((option) => option.value === question.routeTo)?.label ||
                "End Survey";

              return (
                <article
                  key={question.id}
                  ref={(node) => {
                    if (node) {
                      questionRefs.current[question.id] = node;
                    } else {
                      delete questionRefs.current[question.id];
                    }
                  }}
                  className={`${styles.questionCard} ${
                    activeQuestionId === question.id ? styles.questionCardActive : ""
                  }`}
                  onClick={() => setActiveQuestionId(question.id)}
                >
                  <div className={styles.questionHeader}>
                    <div className={styles.questionHeaderLeft}>
                      <div className={styles.questionNumber}>{index + 1}</div>
                      <div className={styles.questionHeaderMeta}>
                        <div className={styles.questionCodeRow}>
                          <span className={styles.questionCode}>{question.code}</span>
                          {guide?.startQuestionId === question.id ? (
                            <span className={styles.startBadge}>Start</span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className={styles.questionControls}>
                      <button
                        type="button"
                        className={
                          guide?.startQuestionId === question.id
                            ? `${styles.iconButton} ${styles.startControlActive}`
                            : styles.iconButton
                        }
                        onClick={() => setStartQuestion(question.id)}
                        aria-label="Set start question"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className={styles.startIcon}
                          aria-hidden="true"
                          fill="currentColor"
                        >
                          <polygon points="8 5 19 12 8 19 8 5" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => moveQuestion(question.id, -1)}
                        disabled={index === 0}
                        aria-label="Move question up"
                      >
                        <span className={styles.moveButtonLabel} aria-hidden="true">
                          ↑
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => moveQuestion(question.id, 1)}
                        disabled={index === guide.questions.length - 1}
                        aria-label="Move question down"
                      >
                        <span className={styles.moveButtonLabel} aria-hidden="true">
                          ↓
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                        onClick={() => removeQuestion(question.id)}
                        aria-label="Delete question"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className={styles.questionBody}>
                    <div className={styles.fieldBlock}>
                      <label className={styles.microLabel}>Section</label>
                      <input
                        className={styles.fieldInput}
                        value={question.section}
                        onChange={(event) =>
                          onQuestionChange(question.id, "section", event.target.value)
                        }
                      />
                    </div>

                    <div className={styles.fieldBlock}>
                      <label className={styles.microLabel}>Question</label>
                      <textarea
                        className={styles.questionTextarea}
                        value={question.prompt}
                        onChange={(event) =>
                          onQuestionChange(question.id, "prompt", event.target.value)
                        }
                      />
                    </div>

                    <div className={styles.answerTypeBlock}>
                      <div className={styles.microLabel}>Answer Type</div>
                      <div className={styles.answerTypeGrid}>
                        {ANSWER_TYPES.map((type) => (
                          <div key={type.value} className={styles.answerTypeOptionGroup}>
                            <button
                              type="button"
                              className={
                                question.answerType === type.value
                                  ? `${styles.answerTypeButton} ${styles.answerTypeButtonActive}`
                                  : styles.answerTypeButton
                              }
                              onClick={() => onQuestionTypeChange(question.id, type.value)}
                            >
                              {type.label}
                            </button>

                            {type.value === "multipleChoice" &&
                            question.answerType === "multipleChoice" ? (
                              <button
                                type="button"
                                className={styles.answerTypeTemplateButton}
                                onClick={() => applyYesNoTemplate(question.id)}
                              >
                                Yes / No
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>

                    {answerTypeMeta.supportsOptions ? (
                      <>
                        <div className={styles.fieldBlock}>
                          <div className={styles.microLabel}>Options & Routing</div>
                          <div className={styles.optionList}>
                            {question.options.map((option) => (
                              <div key={option.id} className={styles.optionRow}>
                                <input
                                  className={styles.fieldInput}
                                  value={option.label}
                                  onChange={(event) =>
                                    updateQuestionOption(
                                      question.id,
                                      option.id,
                                      "label",
                                      event.target.value,
                                    )
                                  }
                                />
                                <div className={styles.optionArrow}>→</div>
                                <select
                                  className={styles.routeSelect}
                                  value={option.routeTo}
                                  onChange={(event) =>
                                    updateQuestionOption(
                                      question.id,
                                      option.id,
                                      "routeTo",
                                      event.target.value,
                                    )
                                  }
                                >
                                  {routeOptions.map((routeOption) => (
                                    <option key={routeOption.value} value={routeOption.value}>
                                      {routeOption.label}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                                  onClick={() => removeQuestionOption(question.id, option.id)}
                                  disabled={question.options.length <= 1}
                                  aria-label="Remove option"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className={styles.fieldBlock}>
                          <button
                            type="button"
                            className={styles.addSmallButton}
                            onClick={() => addQuestionOption(question.id)}
                          >
                            + Add option
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.fieldBlock}>
                        <label className={styles.microLabel}>Then go to</label>
                        <select
                          className={styles.routeSelect}
                          value={question.routeTo}
                          onChange={(event) =>
                            onQuestionChange(question.id, "routeTo", event.target.value)
                          }
                        >
                          {routeOptions.map((routeOption) => (
                            <option key={routeOption.value} value={routeOption.value}>
                              {routeOption.label}
                            </option>
                          ))}
                        </select>
                        <div className={styles.routeHint}>Current route: {defaultRoute}</div>
                      </div>
                    )}

                    <div className={styles.fieldBlock}>
                      <div className={styles.microLabel}>Interviewer Note</div>
                      {question.interviewerNotes[0] ? (
                        <div className={styles.qnoteBox}>
                          <span className={styles.qnoteIco}>
                            <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="16" x2="12" y2="12" />
                              <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                          </span>
                          <input
                            className={styles.fieldInput}
                            data-noteid={question.interviewerNotes[0].id}
                            value={question.interviewerNotes[0].text}
                            placeholder="Instruction shown under the question (e.g. Huwag magbigay ng listahan...)"
                            onChange={(event) =>
                              updateInterviewerNote(
                                question.id,
                                question.interviewerNotes[0].id,
                                event.target.value,
                              )
                            }
                          />
                          <button
                            type="button"
                            className={styles.qnoteRemove}
                            title="Remove note"
                            onClick={() =>
                              removeInterviewerNote(question.id, question.interviewerNotes[0].id)
                            }
                            aria-label="Remove interviewer note"
                          >
                            <svg viewBox="0 0 24 24" strokeWidth="2" fill="none">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.addNoteButton}
                          onClick={() => addInterviewerNote(question.id)}
                        >
                          + Add interviewer note
                        </button>
                      )}
                    </div>

                    {validation.perQuestion[question.id]?.length ? (
                      <div className={styles.questionValidation}>
                        {validation.perQuestion[question.id].map((issue) => (
                          <div key={issue} className={styles.questionValidationItem}>
                            {issue}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>

          <button type="button" className={styles.addQuestionButton} onClick={addQuestion}>
            + Add Question
          </button>
          <button type="button" className={styles.resetButton} onClick={onResetGuide}>
            Reset Draft
          </button>
        </section>

        <aside className={styles.mapPanel}>
          <div className={styles.mapPanelHeader}>
            <div>
              <div className={styles.mapTitleRow}>
                <span className={styles.mapIcon}>◫</span>
                <h2>Question Map</h2>
              </div>
              <div className={styles.mapLegend}>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendSequential}`} />
                  Sequential
                </span>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendBranch}`} />
                  Branch / jump
                </span>
                <span>
                  <i className={`${styles.legendLine} ${styles.legendEnd}`} />
                  End survey
                </span>
              </div>
            </div>
            <button
              type="button"
              className={styles.expandButton}
              onClick={() => setMapExpanded(true)}
            >
              Expand
            </button>
          </div>

          <div ref={inlineMapViewportRef} className={styles.mapViewportRail}>
            {questionCount ? (
              <SurveyFlowCanvas
                graph={flowGraph}
                scale={inlineMapScale}
                variant="inline"
                showBranchLabels={false}
                selectedQuestionId={activeQuestionId}
                startQuestionId={startMapNode?.id}
                onNodeSelect={onMapNodeSelect}
              />
            ) : (
              <div className={styles.mapEmptyState}>
                <strong>Nothing to map yet</strong>
                <span>
                  The flow map appears here as soon as the survey has at least one question.
                </span>
              </div>
            )}
          </div>

          <div className={styles.mapRailHint}>
            Click a node to jump to that question in the editor.
          </div>

          <div className={styles.readinessList}>
            {readinessChecks.map((check) => (
              <div key={check.label} className={styles.readinessRow}>
                <span className={check.passed ? styles.readinessPassed : styles.readinessPending}>
                  {check.passed ? "✓" : "•"}
                </span>
                <span>{check.label}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {mapExpanded ? (
        <div className={styles.mapModalBackdrop} role="presentation">
          <section
            className={styles.mapModal}
            role="dialog"
            aria-modal="true"
            aria-label="Survey flow map"
          >
            <div className={styles.mapModalHeader}>
              <div>
                <div className={styles.mapTitleRow}>
                  <span className={styles.mapIcon}>◫</span>
                  <h2>Survey Flow Map</h2>
                </div>
                <div className={styles.mapLegend}>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendSequential}`} />
                    Sequential
                  </span>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendBranch}`} />
                    Branch / jump
                  </span>
                  <span>
                    <i className={`${styles.legendLine} ${styles.legendEnd}`} />
                    End survey
                  </span>
                </div>
              </div>

              <div className={styles.mapModalControls}>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setExpandedMapScale((prev) => clamp(prev - 0.1, 0.4, 1.6))}
                >
                  Zoom Out
                </button>
                <div className={styles.zoomReadout}>{Math.round(expandedMapScale * 100)}%</div>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setExpandedMapScale((prev) => clamp(prev + 0.1, 0.4, 1.6))}
                >
                  Zoom In
                </button>
                <button type="button" className={styles.expandButton} onClick={fitExpandedMap}>
                  Fit
                </button>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => setMapExpanded(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div ref={expandedMapViewportRef} className={styles.mapViewportExpanded}>
              {questionCount ? (
                <SurveyFlowCanvas
                  graph={flowGraph}
                  scale={expandedMapScale}
                  variant="expanded"
                  showBranchLabels
                  selectedQuestionId={activeQuestionId}
                  startQuestionId={startMapNode?.id}
                  onNodeSelect={onMapNodeSelect}
                />
              ) : (
                <div className={styles.mapEmptyState}>
                  <strong>Nothing to map yet</strong>
                  <span>Add questions to render the full survey flow.</span>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default SurveyBuilderPage;
